import React, {
  useState,
  useEffect,
  useCallback,
  useMemo,
  useRef,
} from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import {
  CheckCircle2,
  MessageCircle,
  MessageSquare,
  Facebook,
  Calendar,
  Rocket,
  IndianRupee,
  Clock3,
  Layers,
  Zap,
  TrendingUp,
  Bot,
  ArrowUpRight,
  X,
} from "lucide-react";
import { motion } from "framer-motion";
import { useAuth } from "../../app/providers/AuthProvider";
import axiosClient from "../../api/axiosClient";
import { toast } from "react-toastify";
import { requestBusinessSelection } from "../../utils/businessSelectionBus";
import ConnectionSummaryCard from "../../components/WhatsApp/ConnectionSummaryCard";
import MetaPinActivationModal from "../../components/modals/MetaPinActivationModal";
import HelpCenterModal from "../../components/helpcenter/HelpCenterModal";
import PageHeader from "../../components/common/PageHeader";

const ESU_INTENT_KEY = "xb_esu_intent";
const ESU_ADDITIONAL_NUMBER_INTENT = "additionalNumber";
const ESU_PIN_TARGET_KEY = "xb_pending_esu_phone_number_id";

const isGuid = v =>
  !!v &&
  /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(v);

function formatUpgradeDate(utcString) {
  if (!utcString) return null;
  const dt = new Date(utcString);
  if (Number.isNaN(dt.getTime())) return null;
  return dt.toLocaleDateString("en-IN", {
    year: "numeric",
    month: "short",
    day: "numeric",
  });
}

function formatBillingCycleLabel(value) {
  const token = String(value || "")
    .trim()
    .toLowerCase();
  if (token === "monthly") return "Monthly";
  if (token === "quarterly") return "Quarterly (3 Months)";
  if (token === "yearly") return "Yearly";
  return "Selected billing cycle";
}

const WALLET_SUMMARY_ENDPOINT = "/payment/wallet";
const WALLET_LEDGER_ENDPOINT = "/payment/wallet/ledger";
const WALLET_TOP_UP_ENDPOINT = "/payment/wallet/top-up/checkout";
const WALLET_SUBSCRIPTION_ACCESS_ENDPOINT = "/payment/subscription/access";
const WALLET_TOP_UP_MIN_AMOUNT = 50;
const WALLET_TOP_UP_PRESETS = [50, 100, 250, 300, 500];
const BILLING_MODE_PREPAID = "PLATFORM_PREPAID";
const BILLING_MODE_DIRECT = "CUSTOMER_DIRECT_BILLING";

const unwrapResponse = res => {
  const body = res?.data ?? {};
  const ok = typeof body.ok === "boolean" ? body.ok : true;
  const data = body?.data ?? body;
  const message = body?.message || data?.message || "";
  return { ok, data, message };
};

const formatCurrency = amount => {
  const value = Number.isFinite(Number(amount)) ? Number(amount) : 0;
  return `₹${new Intl.NumberFormat("en-IN", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(value)}`;
};

const formatDateTime = value => {
  if (!value) return "No top-up yet";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "No top-up yet";
  return date.toLocaleString("en-IN", {
    month: "short",
    day: "numeric",
    hour: "numeric",
    minute: "2-digit",
  });
};

const normalizeBillingMode = value => {
  if (value === 1 || value === "1") return BILLING_MODE_DIRECT;
  if (value === 0 || value === "0") return BILLING_MODE_PREPAID;

  const normalized = String(value || "").trim();

  if (!normalized) return BILLING_MODE_PREPAID;
  if (normalized === BILLING_MODE_PREPAID) return BILLING_MODE_PREPAID;
  if (normalized === BILLING_MODE_DIRECT) return BILLING_MODE_DIRECT;
  if (normalized === "platformPrepaid" || normalized === "PlatformPrepaid") {
    return BILLING_MODE_PREPAID;
  }
  if (
    normalized === "customerDirectBilling" ||
    normalized === "CustomerDirectBilling"
  ) {
    return BILLING_MODE_DIRECT;
  }

  return BILLING_MODE_PREPAID;
};

const getBillingModeLabel = mode =>
  normalizeBillingMode(mode) === BILLING_MODE_DIRECT
    ? "Customer Direct Billing"
    : "Platform Prepaid";

const getBillingModeBadgeTone = mode =>
  normalizeBillingMode(mode) === BILLING_MODE_DIRECT
    ? "bg-amber-50 text-amber-700 ring-amber-200"
    : "bg-slate-100 text-slate-600 ring-slate-200";

function WelcomePageScopeStyles() {
  return (
    <style>{`
      .welcome-page-scope,
      .welcome-page-scope * {
        font-family: "Manrope", "Inter", "Segoe UI", system-ui, -apple-system, sans-serif !important;
      }

      .welcome-page-scope .rounded,
      .welcome-page-scope .rounded-sm,
      .welcome-page-scope .rounded-md,
      .welcome-page-scope .rounded-lg,
      .welcome-page-scope .rounded-xl,
      .welcome-page-scope .rounded-2xl,
      .welcome-page-scope .rounded-3xl {
        border-radius: 10px !important;
      }

      .welcome-page-scope .rounded-full {
        border-radius: 9999px !important;
      }
    `}</style>
  );
}

const StepItem = ({
  step,
  title,
  description,
  status,
  isCompleted,
  isActive,
  isLast = false,
  onClick,
}) => {
  return (
    <button
      type="button"
      onClick={onClick}
      className="group relative flex w-full items-start gap-4 text-left"
    >
      <div className="relative flex w-10 shrink-0 justify-center">
        {!isLast ? (
          <div
            className={`absolute top-10 h-[calc(100%+16px)] w-[2px] ${
              isCompleted ? "bg-emerald-400" : "bg-slate-200"
            }`}
          />
        ) : null}

        <div
          className={`relative z-10 flex h-10 w-10 items-center justify-center rounded-full border-2 text-sm font-bold transition ${
            isCompleted
              ? "border-emerald-500 bg-emerald-500 text-white shadow-[0_0_0_6px_rgba(16,185,129,0.10)]"
              : isActive
                ? "border-emerald-500 bg-white text-emerald-600"
                : "border-slate-300 bg-white text-slate-400"
          }`}
        >
          {isCompleted ? <CheckCircle2 size={16} /> : step}
        </div>
      </div>

      <div className="min-w-0 pb-8">
        <div className="flex items-center gap-2">
          <div
            className={`text-sm font-semibold ${
              isCompleted || isActive ? "text-slate-900" : "text-slate-500"
            }`}
          >
            {title}
          </div>
        </div>

        <div className="mt-1 text-xs leading-5 text-slate-500">
          {description}
        </div>

        <span
          className={`mt-2 inline-flex items-center rounded-full border px-2.5 py-1 text-[10px] font-semibold uppercase tracking-[0.16em] ${
            isCompleted
              ? "border-emerald-200 bg-emerald-50 text-emerald-700"
              : isActive
                ? "border-amber-200 bg-amber-50 text-amber-700"
                : "border-slate-200 bg-slate-50 text-slate-500"
          }`}
        >
          {status}
        </span>
      </div>
    </button>
  );
};

const SetupRail = ({ items }) => {
  return (
    <div className="rounded-[10px] border border-slate-200 bg-white px-5 py-5 shadow-sm">
      <div className="text-[11px] font-semibold uppercase tracking-[0.22em] text-slate-400">
        Setup Journey
      </div>

      <div className="mt-5">
        {items.map((item, index) => (
          <StepItem
            key={item.key}
            step={index + 1}
            title={item.title}
            description={item.description}
            status={item.status}
            isCompleted={item.completed}
            isActive={!item.completed}
            isLast={index === items.length - 1}
            onClick={item.onClick}
          />
        ))}
      </div>
    </div>
  );
};

const ConnectBanner = ({ onConnect, connecting }) => {
  return (
    <div className="relative overflow-hidden rounded-[10px] border border-slate-200 bg-slate-900 shadow-sm">
      <div className="absolute right-0 top-0 h-40 w-40 rounded-full bg-emerald-500/10 blur-3xl" />
      <div className="absolute bottom-0 left-0 h-32 w-32 rounded-full bg-cyan-500/10 blur-3xl" />

      <div className="relative grid gap-6 px-6 py-7 lg:grid-cols-[1fr_180px] lg:items-center">
        <div>
          <div className="inline-flex items-center gap-2 rounded-full border border-rose-400/30 bg-white px-3 py-1.5 text-xs font-semibold text-rose-600">
            <span className="h-2 w-2 rounded-full bg-rose-500" />
            WhatsApp API not connected
          </div>

          <h2 className="mt-4 text-2xl font-semibold leading-tight text-white">
            Connect your{" "}
            <span className="text-emerald-400">WhatsApp Business API</span>
          </h2>

          <p className="mt-3 max-w-2xl text-sm leading-6 text-slate-300">
            Set up your official WhatsApp Business API connection and unlock
            campaigns, team inbox, automation, and daily customer operations
            from one place.
          </p>

          <div className="mt-5 grid gap-3 sm:grid-cols-2">
            {[
              { icon: Layers, label: "Broadcasting" },
              { icon: Clock3, label: "Automation" },
              { icon: MessageCircle, label: "Team Inbox" },
              { icon: Zap, label: "Campaigns" },
            ].map(item => (
              <div key={item.label} className="flex items-center gap-3">
                <div className="flex h-8 w-8 items-center justify-center rounded-[10px] border border-white/10 bg-white/5 text-emerald-400">
                  <item.icon className="h-4 w-4" />
                </div>
                <span className="text-sm font-medium text-slate-200">
                  {item.label}
                </span>
              </div>
            ))}
          </div>

          <div className="mt-6 flex flex-wrap gap-3">
            <button
              type="button"
              onClick={onConnect}
              disabled={connecting}
              className="inline-flex items-center gap-2 rounded-[10px] bg-emerald-600 px-5 py-3 text-sm font-semibold text-white transition hover:bg-emerald-700 disabled:cursor-not-allowed disabled:opacity-60"
            >
              <Facebook size={15} className="fill-white" />
              {connecting ? "Connecting..." : "Connect with Meta"}
            </button>

            <button
              type="button"
              onClick={() =>
                window.open("https://xplorebyte.com/demo", "_blank")
              }
              className="inline-flex items-center gap-2 rounded-[10px] border border-white/10 bg-transparent px-5 py-3 text-sm font-semibold text-white transition hover:border-white/20 hover:bg-white/5"
            >
              <Calendar size={15} />
              Book Demo
            </button>
          </div>
        </div>

        <div className="hidden lg:flex justify-center">
          <div className="flex h-32 w-32 items-center justify-center rounded-full border border-white/10 bg-white/5 shadow-[0_0_40px_rgba(16,185,129,0.08)]">
            <Rocket className="h-14 w-14 text-emerald-400" />
          </div>
        </div>
      </div>
    </div>
  );
};

const WalletCard = ({
  walletLoading,
  walletSummary,
  walletError,
  billingMode,
  isDirectBilling,
  showLowBalanceWarning,
  showNegativeBalanceWarning,
  lastTopUpAtUtc,
  subscriptionEligible,
  subscriptionMessage,
  topUpOpen,
  topUpButtonRef,
  topUpPopoverRef,
  topUpAmount,
  topUpCustomAmount,
  topUpError,
  topUpLoading,
  onToggleTopUp,
  onPresetSelect,
  onCustomAmountChange,
  onTopUpSubmit,
  onCloseTopUp,
}) => {
  return (
    <div className="relative h-full overflow-visible rounded-[10px] border border-slate-200 bg-white p-4 shadow-sm ring-1 ring-slate-100/70">
      <div className="flex items-start justify-between gap-3">
        <div className="flex items-start gap-3">
          <div className="rounded-[10px] border border-emerald-100 bg-emerald-50 p-2 text-emerald-700">
            <IndianRupee className="h-4 w-4" />
          </div>
          <div>
            <div className="text-[11px] font-semibold uppercase tracking-[0.22em] text-slate-400">
              Wallet Balance
            </div>
            <div className="mt-1 flex flex-wrap items-center gap-2">
              <div className="text-sm font-semibold text-slate-900">
                {isDirectBilling ? "Direct billing account" : "Prepaid wallet"}
              </div>
              <span
                className={`rounded-full px-2.5 py-1 text-[10px] font-semibold uppercase tracking-[0.12em] ring-1 ${getBillingModeBadgeTone(
                  billingMode,
                )}`}
              >
                {getBillingModeLabel(billingMode)}
              </span>
            </div>
          </div>
        </div>

        <div className="relative shrink-0">
          {!isDirectBilling ? (
            <button
              ref={topUpButtonRef}
              type="button"
              onClick={onToggleTopUp}
              disabled={!subscriptionEligible}
              className="inline-flex items-center rounded-full bg-emerald-600 px-3.5 py-1.5 text-[11px] font-semibold text-white shadow-sm shadow-emerald-600/20 transition hover:bg-emerald-700 disabled:cursor-not-allowed disabled:opacity-60"
            >
              {topUpOpen ? "Close" : "Add Funds"}
            </button>
          ) : null}

          {topUpOpen && !isDirectBilling ? (
            <div
              ref={topUpPopoverRef}
              className="absolute right-0 top-full z-30 mt-3 w-[380px] max-w-[calc(100vw-4rem)] rounded-[10px] border border-slate-200 bg-white p-4 shadow-xl"
            >
              <div className="flex items-start justify-between gap-3">
                <div>
                  <div className="text-[11px] font-semibold uppercase tracking-[0.22em] text-slate-400">
                    Add Funds
                  </div>
                  <div className="mt-1 text-sm font-semibold text-slate-900">
                    Select or enter an amount
                  </div>
                </div>

                <button
                  type="button"
                  onClick={onCloseTopUp}
                  className="rounded-full p-1 text-slate-400 transition hover:bg-slate-100 hover:text-slate-600"
                >
                  <X size={16} />
                </button>
              </div>

              <div className="mt-4 grid grid-cols-2 gap-2">
                {WALLET_TOP_UP_PRESETS.map(amount => {
                  const isSelected =
                    topUpAmount === amount &&
                    topUpCustomAmount.trim().length === 0;

                  return (
                    <button
                      key={amount}
                      type="button"
                      onClick={() => onPresetSelect(amount)}
                      className={`rounded-[10px] border px-3 py-2 text-sm font-semibold transition ${
                        isSelected
                          ? "border-emerald-400 bg-emerald-50 text-emerald-700"
                          : "border-slate-200 bg-white text-slate-700 hover:border-slate-300 hover:bg-slate-50"
                      }`}
                    >
                      {formatCurrency(amount)}
                    </button>
                  );
                })}
              </div>

              <div className="mt-4 space-y-2">
                <label className="text-[11px] font-semibold uppercase tracking-[0.18em] text-slate-400">
                  Custom Amount
                </label>
                <input
                  type="number"
                  min={WALLET_TOP_UP_MIN_AMOUNT}
                  step="1"
                  value={topUpCustomAmount}
                  onChange={onCustomAmountChange}
                  placeholder={`Enter INR ${WALLET_TOP_UP_MIN_AMOUNT} or more`}
                  className="w-full rounded-[10px] border border-slate-200 px-3 py-2 text-sm text-slate-900 outline-none transition focus:border-emerald-400 focus:ring-2 focus:ring-emerald-100"
                />
              </div>

              {topUpError ? (
                <div className="mt-4 rounded-[10px] border border-rose-200 bg-rose-50 px-3 py-2 text-sm text-rose-700">
                  {topUpError}
                </div>
              ) : null}

              <div className="mt-4 flex items-center justify-between gap-3">
                <div className="flex items-center gap-2 text-xs text-slate-500">
                  <Clock3 className="h-4 w-4 text-slate-400" />
                  Minimum {formatCurrency(WALLET_TOP_UP_MIN_AMOUNT)}
                </div>

                <button
                  type="button"
                  onClick={onTopUpSubmit}
                  disabled={topUpLoading || !subscriptionEligible}
                  className="rounded-[10px] bg-emerald-600 px-4 py-2 text-sm font-semibold text-white transition hover:bg-emerald-700 disabled:cursor-not-allowed disabled:bg-emerald-300"
                >
                  {topUpLoading ? "Processing..." : "Proceed to Pay"}
                </button>
              </div>
            </div>
          ) : null}
        </div>
      </div>

      {walletError ? (
        <div className="mt-3 rounded-[10px] border border-rose-200 bg-rose-50 px-3 py-2 text-sm text-rose-700">
          {walletError}
        </div>
      ) : null}

      {walletLoading ? (
        <div className="mt-3 text-sm text-slate-500">
          Loading wallet summary...
        </div>
      ) : (
        <>
          <div className="mt-4 rounded-[10px] border border-slate-100 bg-slate-50/70 px-4 py-3">
            <div className="text-[11px] font-semibold uppercase tracking-[0.18em] text-slate-400">
              Current balance
            </div>
            <div className="mt-1 text-base font-medium tracking-normal text-slate-800">
              {formatCurrency(walletSummary?.availableBalance ?? 0)}
            </div>
            <div className="mt-1 text-xs text-slate-500">
              {isDirectBilling
                ? "Charges billed directly."
                : "Available for prepaid deductions."}
            </div>
          </div>

          <div className="mt-3 flex flex-wrap items-center justify-between gap-2 border-t border-slate-100 pt-3">
            <div className="flex items-center gap-2 text-[11px] text-slate-500">
              <Clock3 className="h-3.5 w-3.5 text-slate-400" />
              <span>
                Last top-up:{" "}
                <span className="font-medium text-slate-700">
                  {formatDateTime(lastTopUpAtUtc)}
                </span>
              </span>
            </div>
            <span className="rounded-full border border-emerald-100 bg-emerald-50 px-2.5 py-1 text-[10px] font-semibold uppercase tracking-[0.12em] text-emerald-700">
              {walletSummary?.exists ? "Wallet Active" : "Awaiting Top-Up"}
            </span>
          </div>

          {showNegativeBalanceWarning ? (
            <div className="mt-2 rounded-[10px] border border-rose-200 bg-rose-50 px-3 py-2 text-xs font-medium text-rose-700">
              Wallet balance is negative. Add funds to avoid prepaid deduction
              issues.
            </div>
          ) : null}

          {showLowBalanceWarning ? (
            <div className="mt-2 rounded-[10px] border border-amber-200 bg-amber-50 px-3 py-2 text-xs font-medium text-amber-800">
              Low balance. Add funds soon to keep prepaid messaging active.
            </div>
          ) : null}

          {!subscriptionEligible ? (
            <div className="mt-2 rounded-[10px] border border-amber-200 bg-amber-50 px-3 py-2 text-xs text-amber-800">
              {subscriptionMessage}
            </div>
          ) : null}
        </>
      )}
    </div>
  );
};

const QuickActionsCard = ({ onNavigate }) => {
  const actions = [
    {
      label: "Live Inbox",
      description: "Handle customer conversations",
      path: "/app/inbox",
      icon: MessageSquare,
      tone: "emerald",
    },
    {
      label: "Lead Command Centre",
      description: "Review and prioritize leads",
      path: "/app/dashboard/lead-command-centre",
      icon: TrendingUp,
      tone: "amber",
    },
    {
      label: "ChatBot Builder",
      description: "Build customer reply flows",
      path: "/app/automation",
      icon: Bot,
      tone: "cyan",
    },
  ];

  const actionToneClasses = {
    emerald: {
      row: "border-emerald-100 bg-gradient-to-r from-emerald-50/90 to-white hover:border-emerald-200 hover:from-emerald-100/80",
      icon: "bg-white text-emerald-700 border-emerald-200 shadow-sm shadow-emerald-100",
      arrow: "group-hover:text-emerald-600",
    },
    amber: {
      row: "border-amber-100 bg-gradient-to-r from-amber-50/90 to-white hover:border-amber-200 hover:from-amber-100/80",
      icon: "bg-white text-amber-700 border-amber-200 shadow-sm shadow-amber-100",
      arrow: "group-hover:text-amber-600",
    },
    cyan: {
      row: "border-cyan-100 bg-gradient-to-r from-cyan-50/90 to-white hover:border-cyan-200 hover:from-cyan-100/80",
      icon: "bg-white text-cyan-700 border-cyan-200 shadow-sm shadow-cyan-100",
      arrow: "group-hover:text-cyan-600",
    },
  };

  return (
    <div className="h-full rounded-[10px] border border-slate-200 bg-white p-4 shadow-sm ring-1 ring-slate-100/70">
      <div className="flex items-start justify-between gap-3">
        <div>
          <div className="text-[11px] font-semibold uppercase tracking-[0.22em] text-slate-400">
            Quick Actions
          </div>
          <p className="mt-1 text-sm font-medium text-slate-600">
            Jump to the tools your team uses most.
          </p>
        </div>
        <div className="rounded-[10px] border border-slate-100 bg-slate-50 p-2 text-slate-500">
          <Zap className="h-4 w-4" />
        </div>
      </div>

      <div className="mt-4 space-y-2">
        {actions.map(action => {
          const tone = actionToneClasses[action.tone];

          return (
            <button
              key={action.path}
              type="button"
              onClick={() => onNavigate(action.path)}
              className={`group flex w-full items-center gap-3 rounded-[10px] border px-3 py-3 text-left transition hover:shadow-sm ${tone.row}`}
            >
              <span
                className={`flex h-9 w-9 shrink-0 items-center justify-center rounded-[10px] border ${tone.icon}`}
              >
                <action.icon className="h-4 w-4" />
              </span>
              <span className="min-w-0 flex-1">
                <span className="block whitespace-normal text-sm font-semibold leading-5 text-slate-900">
                  {action.label}
                </span>
                <span className="mt-0.5 block whitespace-normal text-[11px] leading-4 text-slate-500">
                  {action.description}
                </span>
              </span>
              <ArrowUpRight
                className={`h-4 w-4 shrink-0 text-slate-300 transition ${tone.arrow}`}
              />
            </button>
          );
        })}
      </div>
    </div>
  );
};

export default function WelcomePage() {
  const auth = useAuth() || {};
  const [pricingTimezoneStatus, setPricingTimezoneStatus] = useState(null);
  const navigate = useNavigate();

  const {
    isLoading,
    businessId: directBusinessId,
    planId: directPlanId,
    business,
  } = auth;

  const businessId =
    directBusinessId || business?.businessId || business?.id || null;
  const requestBusinessContextSelection = () =>
    requestBusinessSelection({
      reason: "business-context",
      message: "Select a business from the topbar to continue.",
      source: window.location.pathname,
      open: false,
    });

  const planId = directPlanId ?? business?.planId ?? null;
  const hasPlan = !!planId;

  const [search] = useSearchParams();

  const [showHelpCenter, setShowHelpCenter] = useState(false);
  const [showUpgradeSuccessModal, setShowUpgradeSuccessModal] = useState(false);
  const [connecting, setConnecting] = useState(false);
  const [upgradeSuccessInfo, setUpgradeSuccessInfo] = useState({
    planName: "",
    billingCycle: "",
    periodEndUtc: "",
    refreshHint: false,
  });

  const [walletSummary, setWalletSummary] = useState(null);
  const [lastTopUpAtUtc, setLastTopUpAtUtc] = useState(null);
  const [walletLoading, setWalletLoading] = useState(true);
  const [walletError, setWalletError] = useState("");
  const [billingMode, setBillingMode] = useState(BILLING_MODE_PREPAID);
  const [topUpOpen, setTopUpOpen] = useState(false);
  const [topUpAmount, setTopUpAmount] = useState(null);
  const [topUpCustomAmount, setTopUpCustomAmount] = useState("");
  const [topUpError, setTopUpError] = useState("");
  const [topUpLoading, setTopUpLoading] = useState(false);
  const [topUpSuccessVisible, setTopUpSuccessVisible] = useState(false);
  const [subscriptionEligible, setSubscriptionEligible] = useState(false);
  const [subscriptionMessage, setSubscriptionMessage] = useState(
    "An active subscription is required before adding wallet funds.",
  );

  const topUpButtonRef = useRef(null);
  const topUpPopoverRef = useRef(null);

  const [waStatus, setWaStatus] = useState({
    loading: true,
    hasSettings: false,
    data: null,
  });

  const [showPinModal, setShowPinModal] = useState(() => {
    return !search.has("esuStatus") &&
      sessionStorage.getItem("xb_pending_esu_pin") === "true";
  });
  const [pinPhoneNumberId, setPinPhoneNumberId] = useState(
    () => sessionStorage.getItem(ESU_PIN_TARGET_KEY) || "",
  );

  const closePinModal = () => {
    setShowPinModal(false);
    sessionStorage.removeItem("xb_pending_esu_pin");
    sessionStorage.removeItem(ESU_PIN_TARGET_KEY);
    setPinPhoneNumberId("");
  };

  const loadBillingMode = useCallback(async () => {
    if (!isGuid(businessId)) {
      setBillingMode(BILLING_MODE_PREPAID);
      return;
    }

    try {
      const { data } = await axiosClient.get(`/businesses/${businessId}`);
      setBillingMode(normalizeBillingMode(data?.billingMode));
    } catch {
      setBillingMode(BILLING_MODE_PREPAID);
    }
  }, [businessId]);

  const loadWalletSummary = useCallback(async () => {
    if (!isGuid(businessId)) {
      setWalletLoading(false);
      setWalletError("");
      setWalletSummary(null);
      return;
    }

    try {
      setWalletLoading(true);
      setWalletError("");

      const res = await axiosClient.get(WALLET_SUMMARY_ENDPOINT);
      const { ok, data, message } = unwrapResponse(res);

      if (!ok) {
        throw new Error(message || "Failed to load wallet summary.");
      }

      setWalletSummary(data);
    } catch (error) {
      const message =
        error?.response?.data?.message ||
        error?.message ||
        "Failed to load wallet summary.";
      setWalletError(message);
    } finally {
      setWalletLoading(false);
    }
  }, [businessId]);

  const loadLatestTopUp = useCallback(async () => {
    if (!isGuid(businessId)) {
      setLastTopUpAtUtc(null);
      return;
    }

    try {
      const res = await axiosClient.get(WALLET_LEDGER_ENDPOINT, {
        params: { page: 1, pageSize: 20 },
      });

      const { ok, data } = unwrapResponse(res);
      if (!ok) {
        setLastTopUpAtUtc(null);
        return;
      }

      const latestCredit = (Array.isArray(data?.items) ? data.items : []).find(
        item =>
          String(item?.entryType || "")
            .trim()
            .toLowerCase() === "credit",
      );

      setLastTopUpAtUtc(latestCredit?.occurredAtUtc || null);
    } catch {
      setLastTopUpAtUtc(null);
    }
  }, [businessId]);

  const loadSubscriptionEligibility = useCallback(async () => {
    if (!isGuid(businessId)) {
      setSubscriptionEligible(false);
      setSubscriptionMessage("Select a business before adding wallet funds.");
      return;
    }

    try {
      const res = await axiosClient.get(WALLET_SUBSCRIPTION_ACCESS_ENDPOINT, {
        headers: { "x-suppress-403-toast": true },
      });

      const { ok, data, message } = unwrapResponse(res);

      if (!ok) {
        setSubscriptionEligible(false);
        setSubscriptionMessage(
          message ||
            "An active subscription is required before adding wallet funds.",
        );
        return;
      }

      const allowed = !!data?.allowed;
      setSubscriptionEligible(allowed);
      setSubscriptionMessage(
        allowed
          ? ""
          : data?.message ||
              message ||
              "An active subscription is required before adding wallet funds.",
      );
    } catch (error) {
      const message =
        error?.response?.data?.data?.message ||
        error?.response?.data?.message ||
        "An active subscription is required before adding wallet funds.";

      setSubscriptionEligible(false);
      setSubscriptionMessage(message);
    }
  }, [businessId]);

  const fetchWaStatus = useCallback(async () => {
    if (!isGuid(businessId)) {
      if (isLoading || !businessId) return;
      setWaStatus({ loading: false, hasSettings: false, data: null });
      return;
    }

    setWaStatus(prev => ({ ...prev, loading: true }));

    try {
      const res = await axiosClient.get("whatsappsettings/me", {
        headers: { "X-Business-Id": businessId },
      });

      const has = !!res?.data?.hasSettings;

      setWaStatus({
        loading: false,
        hasSettings: has,
        data: has ? res.data.data : null,
      });
    } catch {
      setWaStatus({ loading: false, hasSettings: false, data: null });
    }
  }, [businessId, isLoading]);

  useEffect(() => {
    let cancelled = false;

    (async () => {
      if (cancelled) return;
      await fetchWaStatus();
    })();

    return () => {
      cancelled = true;
    };
  }, [fetchWaStatus]);

  useEffect(() => {
    loadWalletSummary();
  }, [loadWalletSummary]);

  useEffect(() => {
    loadLatestTopUp();
  }, [loadLatestTopUp]);

  useEffect(() => {
    loadSubscriptionEligibility();
  }, [loadSubscriptionEligibility]);

  useEffect(() => {
    loadBillingMode();
  }, [loadBillingMode]);

  useEffect(() => {
    if (!topUpOpen || typeof document === "undefined") return undefined;

    const handlePointerDown = event => {
      const target = event.target;
      if (
        topUpPopoverRef.current?.contains(target) ||
        topUpButtonRef.current?.contains(target)
      ) {
        return;
      }

      setTopUpOpen(false);
      setTopUpError("");
    };

    const handleEscape = event => {
      if (event.key !== "Escape") return;
      setTopUpOpen(false);
      setTopUpError("");
    };

    document.addEventListener("mousedown", handlePointerDown);
    document.addEventListener("keydown", handleEscape);

    return () => {
      document.removeEventListener("mousedown", handlePointerDown);
      document.removeEventListener("keydown", handleEscape);
    };
  }, [topUpOpen]);

  const whatsappConnected = waStatus.hasSettings;
  const isDirectBilling = billingMode === BILLING_MODE_DIRECT;

  useEffect(() => {
    if (!isDirectBilling) return;
    setTopUpOpen(false);
    setTopUpError("");
  }, [isDirectBilling]);

  useEffect(() => {
    const params = new URLSearchParams(search);
    if (params.get("upgradeSuccess") !== "1") return;

    setUpgradeSuccessInfo({
      planName: params.get("planName") || "your selected plan",
      billingCycle: params.get("billingCycle") || "",
      periodEndUtc: params.get("periodEndUtc") || "",
      refreshHint: params.get("refreshHint") === "1",
    });

    setShowUpgradeSuccessModal(true);

    const cleaned = new URLSearchParams(search);
    cleaned.delete("upgradeSuccess");
    cleaned.delete("planName");
    cleaned.delete("billingCycle");
    cleaned.delete("periodEndUtc");
    cleaned.delete("refreshHint");
    navigate({ search: cleaned.toString() }, { replace: true });
  }, [navigate, search]);

  useEffect(() => {
    const params = new URLSearchParams(search);
    if (params.get("topupSuccess") !== "1") return;

    setTopUpSuccessVisible(true);
    setTopUpOpen(false);
    setTopUpAmount(null);
    setTopUpCustomAmount("");
    setTopUpError("");
    toast.success("Funds added successfully");
    void loadWalletSummary();
    void loadSubscriptionEligibility();
    void loadLatestTopUp();

    const nextParams = new URLSearchParams(search);
    nextParams.delete("topupSuccess");
    navigate({ search: nextParams.toString() }, { replace: true });
  }, [
    loadLatestTopUp,
    loadSubscriptionEligibility,
    loadWalletSummary,
    navigate,
    search,
  ]);

  useEffect(() => {
    const params = new URLSearchParams(search);
    const rawStatus = params.get("esuStatus");
    const status = rawStatus?.toLowerCase();
    const callbackError = params.get("error");
    const callbackPhoneNumberId = params.get("phoneNumberId") || "";
    const isAdditionalNumber =
      sessionStorage.getItem(ESU_INTENT_KEY) === ESU_ADDITIONAL_NUMBER_INTENT;

    if (status === "success") {
      if (isAdditionalNumber) {
        toast.success("WhatsApp number added successfully.");
      }
      if (!isAdditionalNumber) {
      toast.success("Meta connection saved. Check the setup status below.");
      sessionStorage.removeItem("xb_pending_esu_pin");
      }
      sessionStorage.removeItem("xb_pending_esu_pin");
      sessionStorage.removeItem(ESU_PIN_TARGET_KEY);
      sessionStorage.removeItem(ESU_INTENT_KEY);
      setPinPhoneNumberId("");
      setShowPinModal(false);
      fetchWaStatus();
    } else if (
      status === "needs_pin" ||
      (!rawStatus && sessionStorage.getItem("xb_pending_esu_pin") === "true")
    ) {
      if (status === "needs_pin") {
        if (callbackPhoneNumberId) {
          sessionStorage.setItem(ESU_PIN_TARGET_KEY, callbackPhoneNumberId);
          setPinPhoneNumberId(callbackPhoneNumberId);
        }
        toast.info(
          "This number is already protected. Please enter your existing PIN.",
        );
        sessionStorage.setItem("xb_pending_esu_pin", "true");
        sessionStorage.removeItem(ESU_INTENT_KEY);
      }
      setShowPinModal(true);
    } else if (status === "failed") {
      sessionStorage.removeItem(ESU_INTENT_KEY);
      sessionStorage.removeItem("xb_pending_esu_pin");
      sessionStorage.removeItem(ESU_PIN_TARGET_KEY);
      setPinPhoneNumberId("");
      setShowPinModal(false);
      toast.error(
        callbackError === "ADDITIONAL_NUMBER_WABA_MISMATCH"
          ? "This number belongs to a different WhatsApp Business Account. Select a number from the same account already connected to this workspace."
          : "Meta onboarding could not be completed. Please try again.",
      );
      void fetchWaStatus();
    }

    if (rawStatus || callbackError || callbackPhoneNumberId) {
      const nextParams = new URLSearchParams(search);
      nextParams.delete("esuStatus");
      nextParams.delete("error");
      nextParams.delete("phoneNumberId");
      navigate({ search: nextParams.toString() }, { replace: true });
    }
  }, [fetchWaStatus, navigate, search]);

  const startFacebookConnect = async () => {
    try {
      setConnecting(true);
      const returnUrlAfterSuccess = "/app/welcomepage";

      if (!isGuid(businessId)) {
        requestBusinessContextSelection();
        return;
      }

      const res = await axiosClient.post(
        "esu/facebook/start",
        { returnUrlAfterSuccess },
        { headers: { "X-Business-Id": businessId } },
      );

      const authUrl =
        res?.data?.data?.launchUrl ||
        res?.data?.data?.LaunchUrl ||
        res?.data?.launchUrl ||
        res?.data?.LaunchUrl ||
        res?.data?.data?.authUrl ||
        res?.data?.authUrl ||
        res?.data?.url;

      if (!authUrl) {
        toast.error(
          res?.data?.message || "Could not get Facebook connect URL.",
        );
        return;
      }

      window.location.href = authUrl;
    } catch (err) {
      toast.error(
        err?.response?.data?.message ||
          err?.message ||
          "Failed to start Facebook Connect.",
      );
    } finally {
      setConnecting(false);
    }
  };

  const handlePinSuccess = async () => {
    sessionStorage.removeItem("xb_pending_esu_pin");
    sessionStorage.removeItem(ESU_PIN_TARGET_KEY);
    sessionStorage.removeItem(ESU_INTENT_KEY);
    setPinPhoneNumberId("");
    await fetchWaStatus();
  };

  const openTopUpPanel = useCallback(() => {
    if (!subscriptionEligible) {
      setTopUpOpen(false);
      setTopUpError(subscriptionMessage);
      toast.error(subscriptionMessage);
      return;
    }

    setTopUpOpen(open => !open);
    setTopUpError("");

    if (topUpOpen) {
      setTopUpAmount(null);
      setTopUpCustomAmount("");
    }
  }, [subscriptionEligible, subscriptionMessage, topUpOpen]);

  const resolvedTopUpAmount = useMemo(() => {
    if (topUpCustomAmount.trim()) {
      const parsed = Number(topUpCustomAmount);
      return Number.isFinite(parsed) && Number.isInteger(parsed)
        ? parsed
        : null;
    }

    return Number.isInteger(topUpAmount) ? topUpAmount : null;
  }, [topUpAmount, topUpCustomAmount]);

  const handleTopUpSubmit = useCallback(async () => {
    if (!Number.isInteger(resolvedTopUpAmount)) {
      setTopUpError("Select or enter an amount to add.");
      return;
    }

    if (resolvedTopUpAmount < WALLET_TOP_UP_MIN_AMOUNT) {
      setTopUpError(
        `Enter an amount of INR ${WALLET_TOP_UP_MIN_AMOUNT} or more.`,
      );
      return;
    }

    if (!subscriptionEligible) {
      setTopUpError(subscriptionMessage);
      return;
    }

    try {
      setTopUpLoading(true);
      setTopUpError("");
      setTopUpSuccessVisible(false);

      const res = await axiosClient.post(WALLET_TOP_UP_ENDPOINT, {
        amount: resolvedTopUpAmount,
      });

      const { ok, data, message } = unwrapResponse(res);
      if (!ok) {
        throw new Error(message || "Failed to start wallet top-up.");
      }

      const checkoutSessionId =
        data?.checkoutSessionId || data?.sessionId || data?.id || null;

      if (!checkoutSessionId) {
        throw new Error("Checkout session id not returned by server.");
      }

      navigate(
        `/app/payment/status?source=wallet&checkoutSessionId=${encodeURIComponent(
          checkoutSessionId,
        )}&sessionId=${encodeURIComponent(checkoutSessionId)}`,
      );
    } catch (error) {
      const message =
        error?.response?.data?.message ||
        error?.message ||
        "Failed to start wallet top-up.";
      setTopUpError(message);
      toast.error(message);
    } finally {
      setTopUpLoading(false);
    }
  }, [
    navigate,
    resolvedTopUpAmount,
    subscriptionEligible,
    subscriptionMessage,
  ]);

  const loadingView =
    isLoading || waStatus.loading ? (
      <div className="welcome-page-scope min-h-screen bg-slate-50 px-6 py-8">
        <WelcomePageScopeStyles />
        <div className="mx-auto max-w-6xl space-y-8">
          <div className="h-10 w-72 rounded-xl bg-slate-200/70 animate-pulse" />
          <div className="grid gap-8 lg:grid-cols-[280px_minmax(0,1fr)]">
            <div className="rounded-[10px] border border-slate-200 bg-white p-5 shadow-sm">
              <div className="h-4 w-24 rounded bg-slate-200/70 animate-pulse" />
              <div className="mt-5 space-y-6">
                {[1, 2, 3].map(i => (
                  <div key={i} className="flex gap-4">
                    <div className="h-10 w-10 rounded-full bg-slate-200/70 animate-pulse" />
                    <div className="flex-1 space-y-2">
                      <div className="h-4 w-32 rounded bg-slate-200/70 animate-pulse" />
                      <div className="h-3 w-24 rounded bg-slate-200/70 animate-pulse" />
                    </div>
                  </div>
                ))}
              </div>
            </div>

            <div className="space-y-5">
              <div className="h-56 rounded-[10px] border border-slate-200 bg-white animate-pulse" />
              <div className="grid gap-5 xl:grid-cols-2">
                <div className="h-72 rounded-[10px] border border-slate-200 bg-white animate-pulse" />
                <div className="h-72 rounded-[10px] border border-slate-200 bg-white animate-pulse" />
              </div>
            </div>
          </div>
        </div>
      </div>
    ) : null;

  if (loadingView) {
    return loadingView;
  }

  const showNegativeBalanceWarning =
    !isDirectBilling &&
    !walletLoading &&
    !!walletSummary?.exists &&
    !!walletSummary?.isNegativeBalance;

  const showLowBalanceWarning =
    !isDirectBilling &&
    !walletLoading &&
    !!walletSummary?.exists &&
    !showNegativeBalanceWarning &&
    !!walletSummary?.isLowBalance;

  const setupItems = [
    {
      key: "profile",
      title: "Business Profile",
      description: "Workspace profile and business identity are in place.",
      status: "Created",
      completed: true,
      onClick: () => navigate("/app/settings/profile-completion"),
    },
    {
      key: "whatsapp",
      title: "Connect WhatsApp API",
      description: "Link your business number with Meta.",
      status: whatsappConnected ? pricingTimezoneStatus === "Configured" ? "Connected" : "Billing Setup Pending" : "Action Required",
      completed: whatsappConnected && pricingTimezoneStatus === "Configured",
      onClick: () =>
        whatsappConnected
          ? navigate("/app/settings/integrations")
          : startFacebookConnect(),
    },
    {
      key: "plan",
      title: "Select Plan",
      description: "Enable subscription-based access and scaling.",
      status: hasPlan ? "Plan Active" : "Pending",
      completed: hasPlan,
      onClick: () => navigate("/app/settings/billing"),
    },
  ];

  return (
    <div className="welcome-page-scope min-h-screen bg-slate-50 px-6 pb-8 pt-6 sm:px-8">
      <WelcomePageScopeStyles />

      <div className="mx-auto max-w-6xl space-y-6">
        <PageHeader
          icon={Rocket}
          title="Welcome Center"
          subtitle="A clean starting point for setup, WhatsApp connection, billing access, and wallet status."
          actions={
            <button
              type="button"
              className="inline-flex items-center gap-2 rounded-[10px] border border-slate-200 bg-white px-4 py-2 text-sm font-semibold text-slate-700 shadow-sm transition hover:bg-slate-50"
              onClick={() => setShowHelpCenter(true)}
            >
              <MessageCircle size={16} className="text-emerald-600" />
              Help & Support
            </button>
          }
        />

        {topUpSuccessVisible ? (
          <div className="rounded-[10px] border border-emerald-200 bg-emerald-50 px-5 py-4 text-sm text-emerald-900 shadow-sm">
            <div className="font-semibold">Funds added successfully</div>
            <div className="mt-1 text-emerald-800">
              {isDirectBilling
                ? "Wallet balance has been updated. Direct billing remains active for message charges."
                : "Your wallet balance has been updated for future conversation charges."}
            </div>
          </div>
        ) : null}

        <motion.div
          initial={{ opacity: 0, y: 8 }}
          animate={{ opacity: 1, y: 0 }}
          className="grid gap-8 lg:grid-cols-[280px_minmax(0,1fr)]"
        >
          <div>
            <SetupRail items={setupItems} />
          </div>

          <div className="space-y-5">
            {!whatsappConnected ? (
              <ConnectBanner
                onConnect={startFacebookConnect}
                connecting={connecting}
              />
            ) : (
              <ConnectionSummaryCard key={businessId} showBillingTimeZone
                canManage={!!auth.hasAllAccess || !!auth.hasFeature?.("SETTINGS.META.ACCOUNT.MANAGEMENT")}
                onBillingTimeZoneStatusChange={setPricingTimezoneStatus} />
            )}

            {whatsappConnected ? (
              <div className="grid gap-5 xl:grid-cols-2">
                <QuickActionsCard onNavigate={navigate} />
                <WalletCard
                  walletLoading={walletLoading}
                  walletSummary={walletSummary}
                  walletError={walletError}
                  billingMode={billingMode}
                  isDirectBilling={isDirectBilling}
                  showLowBalanceWarning={showLowBalanceWarning}
                  showNegativeBalanceWarning={showNegativeBalanceWarning}
                  lastTopUpAtUtc={lastTopUpAtUtc}
                  subscriptionEligible={subscriptionEligible}
                  subscriptionMessage={subscriptionMessage}
                  topUpOpen={topUpOpen}
                  topUpButtonRef={topUpButtonRef}
                  topUpPopoverRef={topUpPopoverRef}
                  topUpAmount={topUpAmount}
                  topUpCustomAmount={topUpCustomAmount}
                  topUpError={topUpError}
                  topUpLoading={topUpLoading}
                  onToggleTopUp={openTopUpPanel}
                  onPresetSelect={amount => {
                    setTopUpAmount(amount);
                    setTopUpCustomAmount("");
                    setTopUpError("");
                  }}
                  onCustomAmountChange={event => {
                    setTopUpCustomAmount(event.target.value);
                    setTopUpAmount(null);
                    setTopUpError("");
                  }}
                  onTopUpSubmit={handleTopUpSubmit}
                  onCloseTopUp={() => {
                    setTopUpOpen(false);
                    setTopUpError("");
                  }}
                />
              </div>
            ) : null}
          </div>
        </motion.div>

        <MetaPinActivationModal
          isOpen={showPinModal}
          onClose={closePinModal}
          businessId={businessId}
          phoneNumberId={pinPhoneNumberId}
          onSuccess={handlePinSuccess}
        />

        <HelpCenterModal
          isOpen={showHelpCenter}
          onClose={() => setShowHelpCenter(false)}
        />

        {showUpgradeSuccessModal ? (
          <div className="fixed inset-0 z-[110] flex items-center justify-center bg-slate-900/60 backdrop-blur-sm p-4">
            <div className="w-full max-w-lg rounded-2xl border border-slate-200 bg-white p-6 shadow-2xl">
              <div className="flex items-start gap-3">
                <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-emerald-100 text-emerald-700">
                  <CheckCircle2 size={20} />
                </div>

                <div className="flex-1">
                  <h3 className="text-lg font-bold text-slate-900">
                    Subscription updated successfully
                  </h3>

                  <p className="mt-1 text-sm text-slate-600">
                    Your plan is now set to{" "}
                    <span className="font-semibold text-slate-900">
                      {upgradeSuccessInfo.planName}
                    </span>{" "}
                    on{" "}
                    <span className="font-semibold text-slate-900">
                      {formatBillingCycleLabel(upgradeSuccessInfo.billingCycle)}
                    </span>
                    .
                  </p>

                  {formatUpgradeDate(upgradeSuccessInfo.periodEndUtc) ? (
                    <p className="mt-1 text-sm text-slate-600">
                      This billing period ends on{" "}
                      <span className="font-semibold text-slate-900">
                        {formatUpgradeDate(upgradeSuccessInfo.periodEndUtc)}
                      </span>
                      .
                    </p>
                  ) : null}

                  <p className="mt-3 text-sm text-slate-600">
                    Your new access is being synced. If the new plan does not
                    appear yet, please refresh this page.
                    {upgradeSuccessInfo.refreshHint
                      ? " If it still does not reflect, logout and login again."
                      : ""}
                  </p>
                </div>
              </div>

              <div className="mt-6 flex flex-wrap justify-end gap-2">
                <button
                  type="button"
                  onClick={() => window.location.reload()}
                  className="rounded-lg bg-emerald-600 px-4 py-2 text-sm font-semibold text-white hover:bg-emerald-700"
                >
                  Refresh Now
                </button>
                <button
                  type="button"
                  onClick={() => setShowUpgradeSuccessModal(false)}
                  className="rounded-lg border border-slate-300 px-4 py-2 text-sm font-semibold text-slate-700 hover:bg-slate-50"
                >
                  Continue
                </button>
              </div>
            </div>
          </div>
        ) : null}
      </div>
    </div>
  );
}
